/**
 * 路由骨架(createBrowserRouter)。覆盖对 C web 全 22 屏(web-pages 全集 W-1~W-22)。
 * 基座(008.1)实现 /login + 应用外壳;008.2 浏览撮合、008.3 供给、008.4 交易+履约+售后、
 * 008.5 IM、008.6 资金/收口(收益/收款账户/评价/协议/隐私/个人中心)已接入真实页。
 * 守卫:登录/浏览/协议类页公开,身份/交易/资金/IM 类页 <AuthGuard>;供给管理页再叠进屏 <KycGate>。
 */
import type { ReactElement, ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { AuthGuard } from './AuthGuard';
import { KycGate } from '@/components/ui/KycGate';
import { LoginPage } from '@/pages/login/LoginPage';
import { PlaceholderPage } from '@/pages/placeholder/PlaceholderPage';
// 008.2 浏览撮合(公开)
import { HomePage } from '@/pages/home/HomePage';
import { SearchPage } from '@/pages/search/SearchPage';
import { CategoryDetailPage } from '@/pages/category/CategoryDetailPage';
import { ProductDetailPage } from '@/pages/product/ProductDetailPage';
// 008.3 供给(受守卫 + KYC 门禁)
import { KycVerifyPage } from '@/pages/kyc-verify/KycVerifyPage';
import { PublishPage } from '@/pages/publish/PublishPage';
import { SellerProductsPage } from '@/pages/seller-products/SellerProductsPage';
// 008.4 交易+履约+售后(受守卫)
import { OrderPreviewPage } from '@/pages/order-preview/OrderPreviewPage';
import { CashierPage } from '@/pages/cashier/CashierPage';
import { PaymentSuccessPage } from '@/pages/payment-success/PaymentSuccessPage';
import { OrdersPage } from '@/pages/orders/OrdersPage';
import { OrderDetailPage } from '@/pages/order-detail/OrderDetailPage';
import { CancellationRequestPage } from '@/pages/cancellation-request/CancellationRequestPage';
// 008.5 IM(受守卫)
import { ConversationsPage } from '@/pages/im/ConversationsPage';
import { ChatPage } from '@/pages/im/ChatPage';
// 008.6 资金/收口六屏(W-17~W-22)
import { EarningsPage } from '@/pages/earnings/EarningsPage';
import { WithdrawAccountPage } from '@/pages/withdraw-account/WithdrawAccountPage';
import { ReviewPage } from '@/pages/review/ReviewPage';
import { AgreementPage } from '@/pages/agreement/AgreementPage';
import { PrivacyPolicyPage } from '@/pages/privacy-policy/PrivacyPolicyPage';
import { CustomerServicePage } from '@/pages/customer-service/CustomerServicePage';
import { MePage } from '@/pages/me/MePage';
import { EditProfilePage } from '@/pages/profile-edit/EditProfilePage';

/** 已实现的公开页(008.2 浏览撮合):替换对应占位路由的 element(不套 AuthGuard)。 */
const REAL_PAGES: Record<string, ReactNode> = {
  '/': <HomePage />,
  '/search': <SearchPage />,
  '/category/:id': <CategoryDetailPage />,
  '/product/:id': <ProductDetailPage />,
};

/** 路由声明(WScreen-N → 实现 story)。guarded=需登录。element=已实现真实页(缺省渲染占位)。 */
interface AppRoute {
  path: string;
  title: string;
  screen: string;
  story: string;
  guarded?: boolean;
  /** 已实现的真实页;缺省则渲染占位。 */
  element?: ReactElement;
}

/**
 * 走 shellChildren 的路由(公开页经 REAL_PAGES 覆盖为真实组件,其余仍占位)。
 * 受守卫的真实页(008.3 供给 / 008.4 交易 / 008.5 IM)在下方各自的 children 数组显式声明,
 * 因此不在此列表(避免重复 path)。
 */
const ROUTES: AppRoute[] = [
  { path: '/', title: '首页', screen: 'W-3', story: '008.2' },
  { path: '/search', title: '搜索 / 发现', screen: 'W-4', story: '008.2' },
  { path: '/category/:id', title: '品类详情', screen: 'W-5', story: '008.2' },
  { path: '/product/:id', title: '商品详情', screen: 'W-6', story: '008.2' },
  { path: '/earnings', title: '收益面板', screen: 'W-17', story: '008.6', guarded: true, element: <EarningsPage /> },
  { path: '/withdraw-account', title: '提现 / 收款账户', screen: 'W-18', story: '008.6', guarded: true, element: <WithdrawAccountPage /> },
  { path: '/review/:orderId', title: '发表评价', screen: 'W-19', story: '008.6', guarded: true, element: <ReviewPage /> },
  { path: '/agreement', title: '用户协议', screen: 'W-20', story: '008.6', element: <AgreementPage /> },
  { path: '/privacy-policy', title: '隐私政策', screen: 'W-21', story: '008.6', element: <PrivacyPolicyPage /> },
  { path: '/customer-service', title: '联系客服', screen: 'W-23', story: 'iter-015', element: <CustomerServicePage /> },
  { path: '/me', title: '个人中心', screen: 'W-22', story: '008.6', guarded: true, element: <MePage /> },
  { path: '/profile/edit', title: '编辑资料', screen: 'W-22', story: 'walkthrough', guarded: true, element: <EditProfilePage /> },
];

function placeholderElement(r: AppRoute) {
  const el = r.element ?? <PlaceholderPage title={r.title} screen={r.screen} story={r.story} />;
  return r.guarded ? <AuthGuard>{el}</AuthGuard> : el;
}

const shellChildren: RouteObject[] = ROUTES.map((r) => ({
  index: r.path === '/',
  path: r.path === '/' ? undefined : r.path,
  // 已实现公开页用真实组件,未实现仍走占位。
  element: REAL_PAGES[r.path] ?? placeholderElement(r),
}));

/** 008.3 供给三屏。KYC 页仅登录守卫;发布/管理再叠进屏 KYC 门禁。 */
const supplyChildren: RouteObject[] = [
  {
    path: '/kyc-verify',
    element: (
      <AuthGuard>
        <KycVerifyPage />
      </AuthGuard>
    ),
  },
  {
    path: '/publish',
    element: (
      <AuthGuard>
        <KycGate prompt="发布商品需先完成实名认证。">
          <PublishPage />
        </KycGate>
      </AuthGuard>
    ),
  },
  {
    path: '/seller-products',
    element: (
      <AuthGuard>
        <KycGate prompt="管理商品需先完成实名认证。">
          <SellerProductsPage />
        </KycGate>
      </AuthGuard>
    ),
  },
];

/** 008.4 交易+履约+售后 6 屏(W-9~W-14),均受登录守卫。 */
const txnChildren: RouteObject[] = [
  { path: '/order-preview', element: guarded(<OrderPreviewPage />) },
  { path: '/cashier/:orderId', element: guarded(<CashierPage />) },
  { path: '/payment-success/:orderId', element: guarded(<PaymentSuccessPage />) },
  { path: '/orders', element: guarded(<OrdersPage />) },
  { path: '/orders/:orderId', element: guarded(<OrderDetailPage />) },
  { path: '/cancellation/:orderId', element: guarded(<CancellationRequestPage />) },
];

/** 008.5 IM 两屏(W-15 会话列表 / W-16 聊天),均受登录守卫。 */
const imChildren: RouteObject[] = [
  { path: '/conversations', element: guarded(<ConversationsPage />) },
  { path: '/chat/:conversationId', element: guarded(<ChatPage />) },
];

function guarded(el: ReactNode): ReactNode {
  return <AuthGuard>{el}</AuthGuard>;
}

export const routes: RouteObject[] = [
  // 登录页:独立布局(极简顶栏,不套全站外壳)
  { path: '/login', element: <LoginPage /> },
  // 其余页:全站外壳(Header + Footer)
  {
    element: <AppLayout />,
    children: [
      ...shellChildren,
      ...supplyChildren,
      ...txnChildren,
      ...imChildren,
      // 兜底:未知路径回首页
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
